import { Icon } from "@pepperstone/pepper-ui-kit";
import type { TestDataProps } from "@repo/ui";
import clsx from "clsx";
import type React from "react";
import type { HTMLAttributes } from "react";
import { useCallback, useRef } from "react";
import styles from "./InputFieldBase.module.css";

export type InputFieldBaseProps = {
  label?: React.ReactNode;
  children?: React.ReactNode | React.ReactNode[];
  labelFor?: string;
  error?: React.ReactNode | string;
  hint?: React.ReactNode | string;
  disabled?: boolean;
  /**
   * Forces the focus border on. Use when focus lives inside a child that the
   * container's `:focus-within` cannot observe — e.g. a cross-origin payment
   * iframe (Nuvei hosted fields). Real `<input>` children do not need this.
   */
  focused?: boolean;
  hideErrorIcon?: boolean;
  className?: string;
  classNames?: {
    container?: string;
    inputContainer?: string;
    label?: string;
  };
  size: "sm" | "md" | "lg";
  labelSize?: "sm" | "md" | "lg";
  onFocusableClick?: () => void;
  hideErrorPlaceholder?: boolean;
} & Omit<HTMLAttributes<HTMLDivElement>, "title"> &
  TestDataProps;

export const InputFieldBase = ({
  label,
  hint,
  error,
  disabled,
  focused,
  className,
  classNames,
  hideErrorIcon,
  children,
  labelFor,
  labelSize,
  onFocusableClick,
  size,
  "data-testid": testId,
  hideErrorPlaceholder,
  ...props
}: InputFieldBaseProps) => {
  const container = useRef<HTMLDivElement>(null);
  const handleContainerClick: React.MouseEventHandler<HTMLDivElement> =
    useCallback(() => {
      onFocusableClick
        ? onFocusableClick()
        : (container.current?.firstChild as HTMLInputElement)?.focus();
    }, [onFocusableClick]);

  return (
    <div
      className={clsx(styles.container, className, classNames?.container)}
      data-testid={testId}
      {...props}
    >
      {label && (
        <label
          className={clsx(
            "ps-tw-reset",
            styles.label,
            labelSize && styles[labelSize],
            classNames?.label
          )}
          htmlFor={labelFor}
          onClick={onFocusableClick}
        >
          {label}
        </label>
      )}
      <div
        ref={container}
        onClick={handleContainerClick}
        className={clsx(
          styles.inputContainer,
          error && styles.error,
          focused && styles.focused,
          disabled && styles.disabled,
          styles[size],
          classNames?.inputContainer
        )}
      >
        {children}
      </div>
      <div
        className={clsx(
          "ps-flex ps-flex-col ps-gap-2",
          (!hideErrorPlaceholder || error) && "ps-pt-2"
        )}
      >
        {hint && (
          <div className="ps-text-body-v2-xs ps-text-text-base-secondary">
            {hint}
          </div>
        )}
        <div className="ps-text-body-v2-xs ps-text-text-primary-error ps-flex ps-gap-1 ps-items-center">
          {error ? (
            <>
              {!hideErrorIcon && <Icon icon="alert-triangle" />}
              <span>{error}</span>
            </>
          ) : (
            !hideErrorPlaceholder && <span>&nbsp;</span>
          )}
        </div>
      </div>
    </div>
  );
};
